import {
  formatCoachingScore,
  type CoachingGradingValueType,
} from "@repo/app-types/entities/coachingScore";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  fitDashArray,
  getCoachingGoalTrendAreaFadeStops,
  getCoachingGoalTrendGradientStops,
  getCoachingGoalTrendModel,
  REFERENCE_LABEL_MIN_GAP_PX,
  separateLabelCenters,
  type CoachingGoalTrendSegmentTone,
} from "@repo/app-types/helpers/coachingGoalTrend";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type RefObject,
} from "react";
import { Popover } from "react-aria-components";
import {
  Area,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  Tooltip,
  useActiveTooltipDataPoints,
  usePlotArea,
  useXAxisScale,
  useYAxisScale,
  XAxis,
  YAxis,
  type DotItemDotProps,
} from "recharts";
import { twJoin, twMerge } from "tailwind-merge";
import { clamp } from "../helpers/helpers.js";
import { PopoverArrow } from "../popover/arrow.js";
import { popoverOffsetForArrow, popoverStyle } from "../popover/style.js";
import { CoachingGoalCallPopover } from "./coachingGoalCallPopover.js";
import type {
  CoachingGoalCallPoint,
  CoachingGoalStatusTone,
} from "./coachingGoalTypes.js";

export interface CoachingGoalTrendMeterProps {
  /** Calls in the goal period, oldest first. */
  calls: readonly CoachingGoalCallPoint[];
  gradingValueType: CoachingGradingValueType;
  startedScore: number;
  targetScore: number;
  /**
   * Blended goal-progress score after each call (see
   * `computeCoachingGoalScorePath` in app-types), oldest first — one entry
   * per call, the last entry being the current score. Empty when the period
   * has no calls yet.
   */
  scorePath: readonly number[];
  /** Fraction (0-1) of the goal period elapsed; sets the plotted width. */
  dayProgress: number;
  /** Short encouragement copy rendered in the current-score badge. */
  statusLabel: string;
  statusTone: CoachingGoalStatusTone;
  activeCallId?: string;
  onCallOpen?: (gradingId: GradingId) => void;
}

export const CoachingGoalTrendMeter: FC<CoachingGoalTrendMeterProps> = (
  props,
) => {
  const {
    calls,
    gradingValueType,
    startedScore,
    targetScore,
    scorePath,
    dayProgress,
    statusLabel,
    statusTone,
    activeCallId,
    onCallOpen,
  } = props;

  const gradientIdPrefix = useId().replace(/:/g, "");
  const lineGradientId = `${gradientIdPrefix}-line`;
  const areaGradientId = `${gradientIdPrefix}-area`;
  const areaFadeGradientId = `${gradientIdPrefix}-area-fade`;
  const areaFadeMaskId = `${gradientIdPrefix}-area-fade-mask`;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const size = useContainerSize(containerRef);
  // Recharts establishes its plot bounds before its axis scales are ready. The
  // reporter publishes that pending layout through state, then replaces it with
  // the complete geometry once the scales resolve. HTML overlays consume only
  // the complete snapshot; unlike a ref mutation, its arrival causes the parent
  // to render without waiting for an unrelated interaction (such as hover).
  const [publishedPlotGeometry, setPublishedPlotGeometry] =
    useState<PublishedPlotGeometry>({ latest: null, lastComplete: null });
  const handlePlotGeometryChange = useCallback((geometry: PlotGeometry) => {
    setPublishedPlotGeometry((previous) =>
      arePlotGeometriesEqual(previous.latest, geometry)
        ? previous
        : {
            latest: geometry,
            lastComplete:
              geometry.scale === null ? previous.lastComplete : geometry,
          },
    );
  }, []);
  const model = useMemo(
    () =>
      getCoachingGoalTrendModel({
        dayProgress,
        gradingValueType,
        scorePath,
        startedScore,
        targetScore,
      }),
    [dayProgress, gradingValueType, scorePath, startedScore, targetScore],
  );
  const plotGeometryVersion =
    size === null
      ? null
      : getPlotGeometryVersion({
          chartMax: model.chartMax,
          chartMin: model.chartMin,
          height: size.height,
          width: size.width,
        });
  // Keep the last complete snapshot while Recharts resolves the next size or
  // domain. The replacement arrives in the same rendering cycle, while keeping
  // this snapshot avoids tearing down every HTML overlay between those states.
  const plotGeometry = publishedPlotGeometry.lastComplete;
  const scale = plotGeometry?.scale;
  const plotLayout = plotGeometry?.layout ?? null;
  // A single smooth monotone curve is colored per segment via a horizontal
  // gradient with hard stops at each call, so alternating up/down segments keep
  // one continuous interpolation instead of per-run straight lines.
  const gradientStops = useMemo(
    () => buildTrendGradientStops(model.segmentTones),
    [model.segmentTones],
  );
  // The area fade must be darkest at the trend line and clear at the start line
  // (the area's baseValue) whether the line runs above or below it, so the mask
  // is a diverging gradient with its transparent stop at the baseline.
  const areaBaseValue = clamp(startedScore, model.chartMin, model.chartMax);
  const areaFadeStops = getCoachingGoalTrendAreaFadeStops(
    areaBaseValue,
    model.values,
  );
  // Recharts owns active-point detection (which call is nearest the cursor, or
  // the arrow-key target from its accessibility layer); the meter only tracks
  // the open call and where to anchor its popover. Closing waits a beat so the
  // cursor can travel from the dot into the popover to hover it.
  const [hoveredCallId, setHoveredCallId] = useState<string | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  // Whether a call popover was open on the previous render, so the popover fades
  // in only when it opens from no active dot — not when it moves between dots.
  const wasPopoverOpenRef = useRef(false);
  // Whether the pointer is currently over the popover. Recharts clears its
  // active point the instant the pointer leaves the chart for the popover, which
  // schedules a close on the next render — after the popover's mouse-enter has
  // run. Guarding the close on this ref keeps the popover open regardless of
  // that ordering, so hovering the popover holds it open.
  const pointerOverPopoverRef = useRef(false);
  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);
  useEffect(() => () => clearCloseTimeout(), [clearCloseTimeout]);
  // Open instantly — recharts has already snapped to the nearest call, so
  // there's nothing to debounce. Only the close is delayed, as a short bridge
  // so the cursor can travel from the dot into the popover to hover it.
  const activateCall = useCallback(
    (callId: string) => {
      clearCloseTimeout();
      setHoveredCallId(callId);
    },
    [clearCloseTimeout],
  );
  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      if (pointerOverPopoverRef.current) {
        return;
      }
      setHoveredCallId(null);
    }, CALL_POPOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);
  const handlePopoverMouseEnter = useCallback(() => {
    pointerOverPopoverRef.current = true;
    clearCloseTimeout();
  }, [clearCloseTimeout]);
  const handlePopoverMouseLeave = useCallback(() => {
    pointerOverPopoverRef.current = false;
    scheduleClose();
  }, [scheduleClose]);
  // Recharts reports the active call (via pointer or keyboard) as a call id or
  // null; open on a call, schedule a close when the active point clears.
  const handleActiveCallChange = useCallback(
    (callId: string | null) => {
      if (callId) {
        activateCall(callId);
      } else {
        scheduleClose();
      }
    },
    [activateCall, scheduleClose],
  );

  // Scrolling slides the chart — and the call dot the popover anchors to — out
  // from under the popover, which react-aria positions once at mount and does
  // not follow as the anchor moves by layout, leaving it stranded over the
  // chart. Rather than chase the anchor every frame, dismiss the popover on
  // scroll — the conventional behavior for a hover card whose anchor scrolls
  // away. We listen for the scroll *gesture* (wheel / touch) rather than the
  // `scroll` event because recharts' keyboard accessibility layer scrolls the
  // active dot into view on every arrow step — programmatic scrolls that fire
  // `scroll` but must not close the popover (keyboard nav repositions it by
  // remounting per call). Capture phase catches the gesture wherever it starts,
  // and the listener is attached only while a call is open.
  useEffect(() => {
    if (hoveredCallId === null) {
      return;
    }
    const dismiss = () => {
      // A gesture over the popover itself isn't stranding anything — react-aria
      // already blocks that scroll, and the anchor stays put — so leave it open.
      // Dismissing here only flickers: the popover would unmount and instantly
      // reopen as the dot re-appears under the still-resting cursor.
      if (pointerOverPopoverRef.current) {
        return;
      }
      clearCloseTimeout();
      setHoveredCallId(null);
    };
    const options = { capture: true, passive: true } as const;
    window.addEventListener("wheel", dismiss, options);
    window.addEventListener("touchmove", dismiss, options);
    return () => {
      window.removeEventListener("wheel", dismiss, { capture: true });
      window.removeEventListener("touchmove", dismiss, { capture: true });
    };
  }, [hoveredCallId, clearCloseTimeout]);

  const displayedCallId = activeCallId ?? hoveredCallId;
  const displayedCall =
    calls.find((call) => call.id === displayedCallId) ?? null;
  const displayedCallIndex = displayedCall
    ? calls.findIndex((call) => call.id === displayedCall.id)
    : -1;
  // The popover anchors to the displayed call's plotted point (index 0 is the
  // baseline start, so call i sits at point i + 1).
  const activeXFraction =
    displayedCallIndex >= 0
      ? (model.xFractions[displayedCallIndex + 1] ??
        model.xFractions[model.xFractions.length - 1] ??
        null)
      : null;
  const activeValue =
    displayedCallIndex >= 0
      ? (model.values[displayedCallIndex + 1] ??
        model.values[model.values.length - 1] ??
        null)
      : null;
  // The last plotted point is the current score; the current-score dot and the
  // badge anchor to it.
  const lastFraction = model.xFractions[model.xFractions.length - 1] ?? 0;
  const lastValue = model.values[model.values.length - 1] ?? model.chartMin;

  const isAchieved =
    model.currentScore !== null && model.currentScore >= targetScore;
  const currentLabel = formatCoachingScore(
    model.currentScore ?? startedScore,
    gradingValueType,
  );

  // Resolve the anchored points to pixels from recharts' current scale.
  const terminalX = scale?.xScale(lastFraction);
  const terminalY = scale?.yScale(lastValue);
  const activeX =
    scale && activeXFraction !== null
      ? scale.xScale(activeXFraction)
      : undefined;
  const activeY =
    scale && activeValue !== null ? scale.yScale(activeValue) : undefined;
  const isPlotted =
    plotLayout !== null && terminalX !== undefined && terminalY !== undefined;

  // The Target/Start labels ride recharts' own y-scale (like the badge above) so
  // they can be rendered as HTML overlays with a guaranteed minimum gap between
  // them — the reference lines stay exactly where the data puts them, only the
  // labels are nudged apart when the two lines are too close to fit both labels.
  const referenceLabels =
    scale && plotLayout !== null && size !== null
      ? getReferenceLabelLayout({
          containerHeight: size.height,
          plotRight: plotLayout.plotRight,
          startLineY: scale.yScale(
            clamp(startedScore, model.chartMin, model.chartMax),
          ),
          startText: `Start: ${formatCoachingScore(startedScore, gradingValueType)}`,
          targetLineY: scale.yScale(targetScore),
          targetText: `Target: ${formatCoachingScore(targetScore, gradingValueType)}`,
        })
      : null;
  // The reference lines span the plot, the chart's width less its margins, and
  // fitting both patterns to it makes the Target and Start lines end together.
  const referenceLineLength =
    (size?.width ?? 0) - PLOT_MARGIN_LEFT_PX - REFERENCE_RAIL_WIDTH_PX;
  const targetDashArray = fitDashArray({
    length: referenceLineLength,
    ...TREND_TARGET_DASH,
  });
  const startDashArray = fitDashArray({
    length: referenceLineLength,
    ...TREND_START_DASH,
  });

  const badgeAnchorX = terminalX ?? 0;
  // Keep the badge from riding too high when the line sits near the top, but
  // anchor the floor a fixed distance below the plot top rather than at the plot
  // midpoint. A mid-plot floor drifts downward whenever the chart grows taller
  // to show a below-baseline dip, which dropped the badge for otherwise-identical
  // data; a fixed offset keeps a high current score's badge in the same place
  // regardless of that height change.
  const badgeAnchorY =
    plotLayout === null || terminalY === undefined
      ? 0
      : Math.max(
          terminalY,
          plotLayout.plotTop + CURRENT_SCORE_BADGE_MIN_TOP_OFFSET_PX,
        );
  const shouldFlipBadge =
    plotLayout !== null &&
    terminalX !== undefined &&
    terminalX > plotLayout.plotRight - BADGE_FLIP_DISTANCE_PX;
  const popoverAnchor =
    activeX !== undefined && activeY !== undefined
      ? { x: activeX, y: activeY }
      : null;
  const isPopoverOpen = displayedCall !== null && popoverAnchor !== null;
  // Animate only on the transition into an open popover; a dot-to-dot move
  // remounts to reposition and would otherwise re-fade on every step.
  const shouldAnimatePopover = isPopoverOpen && !wasPopoverOpenRef.current;
  useEffect(() => {
    wasPopoverOpenRef.current = isPopoverOpen;
  }, [isPopoverOpen]);

  // Recharts renders one dot per row; row 0 is the baseline start, rows 1..n are
  // the calls. `callDot` carries every row's value so the dot index maps
  // straight to the data index (no null gaps to reindex around); `callId` lets
  // the active-point reporter map recharts' active row back to a call.
  const dotRows = model.rows.map((row, index) => ({
    ...row,
    callDot: model.values[index] ?? null,
    callId: index >= 1 ? (calls[index - 1]?.id ?? null) : null,
  }));

  // Only the hovered/active call gets a visible dot (with a halo) — the resting
  // line is bare apart from the current-score dot rendered as an HTML overlay.
  // Interaction is driven by the chart's mouse events below, never by handlers
  // on these dots.
  const renderCallDot = (dotProps: DotItemDotProps) => {
    const { cx, cy, index } = dotProps;
    const call = index >= 1 ? calls[index - 1] : undefined;
    if (
      cx === undefined ||
      cy === undefined ||
      call === undefined ||
      call.id !== displayedCallId
    ) {
      return null;
    }

    const color = getTrendToneColor(model.segmentTones[index - 1] ?? "flat");

    return (
      <g key={call.id}>
        <circle
          cx={cx}
          cy={cy}
          fill={color}
          fillOpacity={0.16}
          r={TREND_CALL_DOT_ACTIVE_HALO_RADIUS}
        />
        <circle
          cx={cx}
          cy={cy}
          fill={color}
          r={TREND_CALL_DOT_ACTIVE_RADIUS}
          stroke="white"
          strokeWidth={2}
        />
      </g>
    );
  };

  // The persistent current-score dot at the end of the line — a recharts
  // ReferenceDot so it rides the chart's own scale (cx/cy come from recharts).
  const renderCurrentScoreDot = (dotProps: { cx?: number; cy?: number }) => {
    const { cx, cy } = dotProps;
    if (cx === undefined || cy === undefined) {
      return <g />;
    }

    const color = getTrendToneColor(model.terminalTone);

    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          fill={color}
          fillOpacity={0.12}
          r={TREND_CURRENT_DOT_HALO_RADIUS}
        />
        <circle
          cx={cx}
          cy={cy}
          fill={color}
          r={TREND_CURRENT_DOT_RADIUS}
          stroke="white"
          strokeWidth={TREND_CURRENT_DOT_BORDER_WIDTH}
        />
      </g>
    );
  };

  return (
    <div
      ref={containerRef}
      // `isolate` keeps the chart's overlay z-indexes (badge, confetti) in their
      // own stacking context so they can't paint over ancestor overlays like a
      // modal, while still layering correctly among themselves.
      className="relative isolate w-full overflow-visible"
      style={{
        height: TREND_CHART_HEIGHT,
      }}
    >
      {size === null || plotGeometryVersion === null ? null : (
        <div className="absolute inset-0">
          <ComposedChart
            aria-label="Coaching goal call scores"
            data={dotRows}
            height={size.height}
            margin={{
              bottom: PLOT_MARGIN_BOTTOM_PX,
              left: PLOT_MARGIN_LEFT_PX,
              right: REFERENCE_RAIL_WIDTH_PX,
              top: PLOT_MARGIN_TOP_PX,
            }}
            width={size.width}
          >
            <defs>
              {/* Horizontal gradients keyed to the calls: hard color stops at
                  each segment boundary tint the single smooth curve/area per
                  segment (green when the average held/rose, red when it fell). */}
              <linearGradient id={lineGradientId} x1="0" y1="0" x2="1" y2="0">
                {gradientStops.map((stop, index) => (
                  <stop
                    key={`line-stop-${index}`}
                    offset={`${stop.offset * 100}%`}
                    stopColor={stop.color}
                  />
                ))}
              </linearGradient>
              <linearGradient id={areaGradientId} x1="0" y1="0" x2="1" y2="0">
                {gradientStops.map((stop, index) => (
                  <stop
                    key={`area-stop-${index}`}
                    offset={`${stop.offset * 100}%`}
                    stopColor={stop.color}
                    stopOpacity={TREND_AREA_FADE_TOP_OPACITY}
                  />
                ))}
              </linearGradient>
              {/* Diverging mask: fades the area's per-segment color to clear at
                  the start line and back to full at the trend line, so the fill
                  reads as a soft glow hugging the line above or below the
                  baseline. Object bounding-box units span the area's own height. */}
              <linearGradient
                id={areaFadeGradientId}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                {areaFadeStops.map((stop, index) => (
                  <stop
                    key={`area-fade-stop-${index}`}
                    offset={`${stop.offset * 100}%`}
                    stopColor="white"
                    stopOpacity={stop.opacity}
                  />
                ))}
              </linearGradient>
              <mask id={areaFadeMaskId} maskContentUnits="objectBoundingBox">
                <rect
                  x="0"
                  y="0"
                  width="1"
                  height="1"
                  fill={`url(#${areaFadeGradientId})`}
                />
              </mask>
            </defs>
            <XAxis dataKey="x" domain={[0, 1]} hide type="number" />
            <YAxis
              domain={[model.chartMin, model.chartMax]}
              hide
              type="number"
            />
            {/* Drives recharts' active-point state (pointer + keyboard);
                renders no visible tooltip of its own. */}
            <Tooltip content={() => null} cursor={false} />
            <CoachingGoalActiveCallReporter
              onActiveCallChange={handleActiveCallChange}
            />
            <CoachingGoalPlotGeometryReporter
              geometryVersion={plotGeometryVersion}
              onGeometryChange={handlePlotGeometryChange}
            />
            {/* The Target/Start labels are rendered as HTML overlays below (not
                recharts reference-line labels) so their vertical positions can be
                nudged apart when the two lines sit close together — otherwise the
                labels overlap whenever the start/target scores are near each
                other or a wide value range compresses the domain. */}
            <ReferenceLine
              stroke={TREND_TARGET_COLOR}
              strokeDasharray={targetDashArray}
              strokeLinecap="round"
              strokeWidth={TREND_REFERENCE_LINE_STROKE_WIDTH}
              y={targetScore}
            />
            <ReferenceLine
              stroke={TREND_START_COLOR}
              strokeDasharray={startDashArray}
              strokeLinecap="round"
              strokeWidth={TREND_REFERENCE_LINE_STROKE_WIDTH}
              y={clamp(startedScore, model.chartMin, model.chartMax)}
            />
            <Area
              activeDot={false}
              baseValue={areaBaseValue}
              dataKey="value"
              dot={false}
              fill={`url(#${areaGradientId})`}
              fillOpacity={1}
              isAnimationActive={false}
              mask={`url(#${areaFadeMaskId})`}
              stroke="none"
              type="monotone"
            />
            <Line
              activeDot={false}
              dataKey="value"
              dot={false}
              isAnimationActive={false}
              stroke={`url(#${lineGradientId})`}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={TREND_CURRENT_LINE_STROKE_WIDTH}
              type="monotone"
            />
            <Line
              activeDot={false}
              dataKey="callDot"
              dot={renderCallDot}
              isAnimationActive={false}
              legendType="none"
              stroke="none"
              type="linear"
            />
            <ReferenceDot
              shape={renderCurrentScoreDot}
              x={lastFraction}
              y={lastValue}
            />
          </ComposedChart>
        </div>
      )}

      {referenceLabels === null ? null : (
        <>
          <span
            className="pointer-events-none absolute z-20 whitespace-nowrap tabular-nums"
            style={{
              left: `${referenceLabels.left}px`,
              top: `${referenceLabels.target.y}px`,
              transform: "translateY(-50%)",
              color: TREND_TARGET_LABEL_COLOR,
              fontSize: REFERENCE_LABEL_FONT_SIZE,
              fontWeight: REFERENCE_LABEL_FONT_WEIGHT,
            }}
          >
            {referenceLabels.target.text}
          </span>
          <span
            className="pointer-events-none absolute z-20 whitespace-nowrap tabular-nums"
            style={{
              left: `${referenceLabels.left}px`,
              top: `${referenceLabels.start.y}px`,
              transform: "translateY(-50%)",
              color: TREND_START_LABEL_COLOR,
              fontSize: REFERENCE_LABEL_FONT_SIZE,
              fontWeight: REFERENCE_LABEL_FONT_WEIGHT,
            }}
          >
            {referenceLabels.start.text}
          </span>
        </>
      )}

      {!isPlotted ? null : (
        <>
          <span
            className={twJoin(
              "pointer-events-none absolute z-30 flex items-center gap-1 rounded-md border px-2 py-0.5 text-left whitespace-nowrap shadow-lg shadow-slate-900/10",
              isAchieved
                ? "border-emerald-500 bg-emerald-500 text-white"
                : "border-border-strong bg-card",
            )}
            style={{
              left: `${badgeAnchorX}px`,
              top: `${badgeAnchorY}px`,
              transform: getCurrentScoreBadgeTransform({
                shouldFlip: shouldFlipBadge,
              }),
            }}
          >
            <span
              className={twJoin(
                "text-[11px] leading-3 font-semibold",
                isAchieved
                  ? "text-white"
                  : statusTone === "danger"
                    ? "text-red-700"
                    : statusTone === "success"
                      ? "text-green-700"
                      : "text-slate-500",
              )}
            >
              {statusLabel}
            </span>
            <span
              className={twJoin(
                "text-base leading-5 font-semibold tracking-normal tabular-nums",
                isAchieved ? "text-white" : "text-gray-900",
              )}
            >
              {currentLabel}
            </span>
          </span>

          {displayedCall !== null && popoverAnchor !== null ? (
            <CoachingGoalCallPopoverAnchor
              // Remount per call so react-aria repositions the popover on the
              // new anchor (it positions at mount and does not follow a trigger
              // that only moves via CSS). `isAnimated` gates the fade so only the
              // open-from-nothing transition animates, not the per-dot remount.
              key={displayedCall.id}
              anchor={popoverAnchor}
              call={displayedCall}
              gradingValueType={gradingValueType}
              isAnimated={shouldAnimatePopover}
              onMouseEnter={handlePopoverMouseEnter}
              onMouseLeave={handlePopoverMouseLeave}
              onCallOpen={onCallOpen}
            />
          ) : null}

          {isAchieved ? <CoachingGoalConfetti /> : null}
        </>
      )}
    </div>
  );
};

interface CoachingGoalActiveCallReporterProps {
  onActiveCallChange: (callId: string | null) => void;
}

/**
 * Bridges recharts' active data point — updated by both pointer hover and the
 * accessibility layer's arrow-key navigation — to the meter's popover state, so
 * the same call popover opens whichever way the user reaches a call. Must be
 * rendered inside the chart to read its context; renders nothing itself.
 */
const CoachingGoalActiveCallReporter: FC<
  CoachingGoalActiveCallReporterProps
> = ({ onActiveCallChange }) => {
  const activePoints = useActiveTooltipDataPoints<{ callId?: string | null }>();
  const activeCallId = activePoints?.[0]?.callId ?? null;

  useEffect(() => {
    onActiveCallChange(activeCallId);
  }, [activeCallId, onActiveCallChange]);

  return null;
};

/** recharts' data-to-pixel scale for one axis (domain value → pixel). */
type PlotAxisScale = (value: number) => number | undefined;

interface PlotScale {
  xScale: PlotAxisScale;
  yScale: PlotAxisScale;
}

interface PlotLayout {
  plotLeft: number;
  plotRight: number;
  plotTop: number;
  plotBottom: number;
}

interface PlotGeometry {
  version: string;
  scale: PlotScale | null;
  layout: PlotLayout;
}

interface PublishedPlotGeometry {
  latest: PlotGeometry | null;
  lastComplete: PlotGeometry | null;
}

interface CoachingGoalPlotGeometryReporterProps {
  geometryVersion: string;
  onGeometryChange: (geometry: PlotGeometry) => void;
}

/**
 * Publishes recharts' plot rectangle immediately, then republishes it with the
 * axis scales as one ready snapshot. The pending state supplies the render in
 * which Recharts' scales become available; the complete state then renders the
 * HTML overlays (current-score badge, labels, call popover). Must render inside
 * the chart to read its context; renders nothing itself.
 */
const CoachingGoalPlotGeometryReporter: FC<
  CoachingGoalPlotGeometryReporterProps
> = (props) => {
  const { geometryVersion, onGeometryChange } = props;
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();
  const plotArea = usePlotArea();

  const plotLeft = plotArea?.x;
  const plotWidth = plotArea?.width;
  const plotTop = plotArea?.y;
  const plotHeight = plotArea?.height;

  useEffect(() => {
    if (
      plotLeft === undefined ||
      plotWidth === undefined ||
      plotTop === undefined ||
      plotHeight === undefined
    ) {
      return;
    }

    onGeometryChange({
      version: geometryVersion,
      scale: xScale && yScale ? { xScale, yScale } : null,
      layout: {
        plotLeft,
        plotRight: plotLeft + plotWidth,
        plotTop,
        plotBottom: plotTop + plotHeight,
      },
    });
  }, [
    geometryVersion,
    onGeometryChange,
    plotHeight,
    plotLeft,
    plotTop,
    plotWidth,
    xScale,
    yScale,
  ]);

  return null;
};

function getPlotGeometryVersion({
  chartMax,
  chartMin,
  height,
  width,
}: {
  chartMax: number;
  chartMin: number;
  height: number;
  width: number;
}): string {
  return `${width}:${height}:${chartMin}:${chartMax}`;
}

function arePlotGeometriesEqual(
  previous: PlotGeometry | null,
  next: PlotGeometry,
): boolean {
  return (
    previous !== null &&
    previous.version === next.version &&
    previous.layout.plotLeft === next.layout.plotLeft &&
    previous.layout.plotRight === next.layout.plotRight &&
    previous.layout.plotTop === next.layout.plotTop &&
    previous.layout.plotBottom === next.layout.plotBottom &&
    (previous.scale === null) === (next.scale === null)
  );
}

interface CoachingGoalCallPopoverAnchorProps {
  anchor: { x: number; y: number };
  call: CoachingGoalCallPoint;
  gradingValueType: CoachingGradingValueType;
  /** Whether the popover fades in on mount (only true when opening from none). */
  isAnimated: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onCallOpen?: (gradingId: GradingId) => void;
}

/**
 * Renders the call popover, anchored to a zero-size element placed at the active
 * dot's coordinate. react-aria owns positioning, flipping, and the arrow;
 * hovering the popover keeps it open (there is no pinning).
 *
 * The open state is fully owned by the meter (the anchor only mounts while a
 * call is active), so react-aria's own dismiss heuristics are deliberately not
 * wired up: keyboard navigation keeps focus on the chart — outside the popover —
 * which react-aria's non-modal outside-focus/interaction detection would treat
 * as a reason to close, fighting the meter and closing the popover on every
 * arrow step. The meter closes it instead when the active point clears.
 */
const CoachingGoalCallPopoverAnchor: FC<CoachingGoalCallPopoverAnchorProps> = (
  props,
) => {
  const {
    anchor,
    call,
    gradingValueType,
    isAnimated,
    onMouseEnter,
    onMouseLeave,
    onCallOpen,
  } = props;
  const anchorRef = useRef<HTMLDivElement | null>(null);
  // Capture whether to animate at mount and hold it for this instance's life:
  // the parent flips `isAnimated` false on the next render, which would strip
  // the transition mid-fade. A per-dot remount (keyed) re-captures it.
  const [isAnimatedAtMount] = useState(isAnimated);

  return (
    <>
      {/* A zero-size trigger makes react-aria mis-center the arrow; sizing the
          anchor to the arrow's width (size-4 = 16px) and centering it on the
          dot lands the arrow exactly on the point. */}
      <div
        ref={anchorRef}
        aria-hidden
        className="pointer-events-none absolute size-4 -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${anchor.x}px`, top: `${anchor.y}px` }}
      />
      <Popover
        className={twMerge(
          popoverStyle({ animated: isAnimatedAtMount }).popover(),
          "z-40",
        )}
        isNonModal
        isOpen
        offset={popoverOffsetForArrow}
        // The hover-keep handlers live on the whole popover, not just the card,
        // so resting the pointer on the arrow (which sits over the chart, above
        // the dot) still holds it open instead of letting the close timer fire
        // and flash the popover as the pointer falls back onto the dot.
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        placement="top"
        triggerRef={anchorRef}
      >
        <PopoverArrow />
        <CoachingGoalCallPopover
          call={call}
          gradingValueType={gradingValueType}
          onCallOpen={onCallOpen}
        />
      </Popover>
    </>
  );
};

const CoachingGoalConfetti: FC = () => (
  <div aria-hidden className="pointer-events-none absolute inset-0 z-10">
    {COACHING_GOAL_CONFETTI_PIECES.map((piece, index) => (
      <span
        key={`${piece.left}-${piece.travelX}-${index}`}
        className="absolute block h-3 w-2 rounded-[2px] opacity-0 motion-safe:animate-coaching-goal-confetti"
        style={
          {
            "--coaching-goal-confetti-rotate": `${piece.rotate}deg`,
            "--coaching-goal-confetti-x": `${piece.travelX}px`,
            "--coaching-goal-confetti-y": `${piece.travelY}px`,
            animationDelay: `${piece.delay}ms`,
            backgroundColor: piece.color,
            left: `${piece.left}%`,
            top: COACHING_GOAL_CONFETTI_ORIGIN_TOP,
          } as CSSProperties
        }
      />
    ))}
  </div>
);

/**
 * Measures the rendered size of the meter container so the chart gets explicit
 * pixel dimensions (recharts needs them, and ResponsiveContainer renders nothing
 * until it self-measures, which breaks headless tests). Overlay coordinates come
 * from recharts' scale, not from this.
 */
function useContainerSize(ref: RefObject<HTMLDivElement | null>) {
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null,
  );

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const update = () => {
      const next = {
        width: element.clientWidth,
        height: element.clientHeight,
      };
      setSize((previous) =>
        previous !== null &&
        previous.width === next.width &&
        previous.height === next.height
          ? previous
          : next,
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}

/**
 * Hard-stop gradient stops (offset 0-1 along x) that color each plotted segment
 * by its tone. Two stops per boundary give a crisp color switch at each call
 * instead of a blended fade. Points are evenly spaced, so segment `i` spans
 * `[i/L, (i+1)/L]` where `L` is the segment count.
 */
function buildTrendGradientStops(
  segmentTones: readonly CoachingGoalTrendSegmentTone[],
): { offset: number; color: string }[] {
  return getCoachingGoalTrendGradientStops(segmentTones).map((stop) => ({
    offset: stop.offset,
    color: getTrendToneColor(stop.tone),
  }));
}

function getTrendToneColor(tone: CoachingGoalTrendSegmentTone) {
  if (tone === "declining") {
    return TREND_DANGER_COLOR;
  }

  return TREND_SUCCESS_COLOR;
}

function getCurrentScoreBadgeTransform({
  shouldFlip,
}: {
  shouldFlip: boolean;
}) {
  return shouldFlip
    ? `translate(calc(-100% - ${CURRENT_SCORE_BADGE_FLIPPED_INLINE_GAP}), calc(-100% - ${CURRENT_SCORE_BADGE_VERTICAL_GAP}))`
    : `translate(-50%, calc(-100% - ${CURRENT_SCORE_BADGE_VERTICAL_GAP}))`;
}

interface ReferenceLabelLayout {
  /** Shared left edge of both labels, just right of the plot area. */
  left: number;
  target: { text: string; y: number };
  start: { text: string; y: number };
}

/**
 * Positions the Target/Start reference labels to the right of the plot, aligned
 * to their lines but held at least {@link REFERENCE_LABEL_MIN_GAP_PX} apart so
 * the two never overlap when the lines sit close together (near start/target
 * scores, or a wide value range compressing the y domain). Returns null until
 * recharts has reported both line positions.
 */
function getReferenceLabelLayout({
  containerHeight,
  plotRight,
  startLineY,
  startText,
  targetLineY,
  targetText,
}: {
  containerHeight: number;
  plotRight: number;
  startLineY: number | undefined;
  startText: string;
  targetLineY: number | undefined;
  targetText: string;
}): ReferenceLabelLayout | null {
  if (targetLineY === undefined || startLineY === undefined) {
    return null;
  }

  const halfHeight = REFERENCE_LABEL_LINE_HEIGHT_PX / 2;
  const minCenter = halfHeight;
  const maxCenter = containerHeight - halfHeight;
  const resolved = separateLabelCenters({
    a: targetLineY,
    b: startLineY,
    minGap: REFERENCE_LABEL_MIN_GAP_PX,
    minCenter,
    maxCenter,
  });

  return {
    left: plotRight + REFERENCE_LABEL_LEFT_GAP_PX,
    target: { text: targetText, y: resolved.a },
    start: { text: startText, y: resolved.b },
  };
}

export const TREND_CHART_HEIGHT = "6rem";
const PLOT_MARGIN_TOP_PX = 16;
const PLOT_MARGIN_BOTTOM_PX = 16;
const PLOT_MARGIN_LEFT_PX = 4;
// Right margin reserved for the recharts reference-line labels ("Target: …").
const REFERENCE_RAIL_WIDTH_PX = 96;
const REFERENCE_LABEL_FONT_SIZE = 12;
const REFERENCE_LABEL_FONT_WEIGHT = 600;
// Horizontal gap from the plot's right edge to the reference labels, matching
// the small inset recharts' own "right"-positioned labels used.
const REFERENCE_LABEL_LEFT_GAP_PX = 6;
// Rendered line box of a 12px label; used to keep nudged labels on-canvas.
const REFERENCE_LABEL_LINE_HEIGHT_PX = 14;
const BADGE_FLIP_DISTANCE_PX = 72;
const CURRENT_SCORE_BADGE_FLIPPED_INLINE_GAP = "0.75rem";
// Half the default plot's inner height (default container 5.25rem − 32px
// margins ≈ 52px). Matches the previous mid-plot floor for the default height
// while staying constant when the chart grows for a below-baseline dip.
const CURRENT_SCORE_BADGE_MIN_TOP_OFFSET_PX = 26;
const CURRENT_SCORE_BADGE_VERTICAL_GAP = "12px";
const TREND_CURRENT_LINE_STROKE_WIDTH = 2;
const TREND_REFERENCE_LINE_STROKE_WIDTH = 1.5;
// Opacity of the area tint at the top of the fill (right under the line); the
// vertical mask fades it to fully transparent at the baseline.
const TREND_AREA_FADE_TOP_OPACITY = 0.11;
const TREND_START_DASH = { dash: 4, gap: 5 };
const TREND_TARGET_DASH = { dash: 5, gap: 5 };
const TREND_CALL_DOT_ACTIVE_RADIUS = 5;
const TREND_CALL_DOT_ACTIVE_HALO_RADIUS = 9;
const TREND_CURRENT_DOT_RADIUS = 6;
const TREND_CURRENT_DOT_HALO_RADIUS = 10;
const TREND_CURRENT_DOT_BORDER_WIDTH = 2.5;
const CALL_POPOVER_CLOSE_DELAY_MS = 260;
const COACHING_GOAL_CONFETTI_ORIGIN_TOP = "36%";
const TREND_SUCCESS_COLOR = "var(--color-emerald-500)";
const TREND_DANGER_COLOR = "var(--color-red-600)";
const TREND_TARGET_COLOR = "var(--color-slate-800)";
const TREND_START_COLOR = "var(--color-gray-300)";
const TREND_TARGET_LABEL_COLOR = "var(--color-slate-800)";
const TREND_START_LABEL_COLOR = "var(--color-gray-500)";
const COACHING_GOAL_CONFETTI_PIECES = [
  {
    color: TREND_SUCCESS_COLOR,
    delay: 0,
    left: 22,
    rotate: -280,
    travelX: -96,
    travelY: -96,
  },
  {
    color: "var(--color-emerald-300)",
    delay: 18,
    left: 31,
    rotate: 220,
    travelX: -62,
    travelY: -132,
  },
  {
    color: "var(--color-amber-400)",
    delay: 36,
    left: 42,
    rotate: -420,
    travelX: -18,
    travelY: -116,
  },
  {
    color: TREND_SUCCESS_COLOR,
    delay: 54,
    left: 53,
    rotate: 380,
    travelX: 28,
    travelY: -138,
  },
  {
    color: TREND_SUCCESS_COLOR,
    delay: 72,
    left: 64,
    rotate: -180,
    travelX: 72,
    travelY: -118,
  },
  {
    color: "var(--color-amber-400)",
    delay: 90,
    left: 74,
    rotate: 460,
    travelX: 112,
    travelY: -92,
  },
  {
    color: "var(--color-emerald-300)",
    delay: 116,
    left: 36,
    rotate: 280,
    travelX: -118,
    travelY: -72,
  },
  {
    color: TREND_SUCCESS_COLOR,
    delay: 132,
    left: 48,
    rotate: -520,
    travelX: -44,
    travelY: -104,
  },
  {
    color: TREND_SUCCESS_COLOR,
    delay: 148,
    left: 58,
    rotate: 320,
    travelX: 54,
    travelY: -108,
  },
  {
    color: "var(--color-amber-400)",
    delay: 166,
    left: 68,
    rotate: -360,
    travelX: 98,
    travelY: -78,
  },
  {
    color: TREND_SUCCESS_COLOR,
    delay: 194,
    left: 28,
    rotate: 520,
    travelX: -132,
    travelY: -86,
  },
  {
    color: "var(--color-emerald-300)",
    delay: 214,
    left: 39,
    rotate: -240,
    travelX: -76,
    travelY: -126,
  },
  {
    color: "var(--color-amber-400)",
    delay: 232,
    left: 50,
    rotate: 600,
    travelX: 8,
    travelY: -142,
  },
  {
    color: TREND_SUCCESS_COLOR,
    delay: 252,
    left: 61,
    rotate: -460,
    travelX: 78,
    travelY: -128,
  },
  {
    color: TREND_SUCCESS_COLOR,
    delay: 274,
    left: 72,
    rotate: 300,
    travelX: 136,
    travelY: -82,
  },
  {
    color: "var(--color-amber-400)",
    delay: 296,
    left: 45,
    rotate: -620,
    travelX: -24,
    travelY: -92,
  },
  {
    color: "var(--color-emerald-300)",
    delay: 318,
    left: 55,
    rotate: 440,
    travelX: 42,
    travelY: -118,
  },
  {
    color: TREND_SUCCESS_COLOR,
    delay: 340,
    left: 66,
    rotate: -300,
    travelX: 112,
    travelY: -108,
  },
] as const;
